import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDramaDetail, useChapters } from '@hook/useApi';
import { api, getUserToken } from '@utils/api';
import { formatDuration, updatePageTitle, updateMetaDescription, isMobile, getCoverImageUrl } from '@utils/helpers';
import BannerAds from '@components/Banner/BannerAds';
import { useVIPStatus } from '@hooks/useVIPStatus';
import { useVideoAds } from '@hooks/useVideoAds';
import '../assets/css/player-ads.css';

function Player() {
    const [searchParams] = useSearchParams();
    const bookId = searchParams.get('bookId');
    const title = searchParams.get('title');
    const startChapter = searchParams.get('startChapter');
    const mobileParam = searchParams.get('mobile');

    const [currentIndex, setCurrentIndex] = useState(0);
    const [episodes, setEpisodes] = useState([]);
    const [isMobileMode, setIsMobileMode] = useState(mobileParam === '1' || isMobile());
    const [showEpisodeList, setShowEpisodeList] = useState(false);
    const [autoPlay, setAutoPlay] = useState(true);
    const videoRef = useRef(null);
    
    // In-player ads system
    const { isVIP } = useVIPStatus();
    const { ads } = useVideoAds();
    const [episodeWatchCount, setEpisodeWatchCount] = useState(0);
    const [isPlayingAd, setIsPlayingAd] = useState(false);
    const [currentAd, setCurrentAd] = useState(null);
    const [pendingEpisodeIndex, setPendingEpisodeIndex] = useState(null);
    
    // Track if current episode has been watched (to prevent skipping ads)
    const [currentEpisodeWatched, setCurrentEpisodeWatched] = useState(false);
    const watchTimeRef = useRef(0); // Track watch time in seconds
    const lastLoggedTime = useRef(-1); // Track last logged time to avoid spam
    const watchTimeHandlerRef = useRef(null); // Store handler for cleanup
    
    // Smart error detection
    const [videoErrorCount, setVideoErrorCount] = useState(0);
    const [showResetSuggestion, setShowResetSuggestion] = useState(false);
    const [lastErrorMessage, setLastErrorMessage] = useState('');
    
    // Reset cooldown (10 minutes)
    const [canReset, setCanReset] = useState(true);
    const [resetCooldownTime, setResetCooldownTime] = useState(0);

    // Watch history tracking (throttle saves)
    const lastSaveTimeRef = useRef(0);
    const userToken = getUserToken();

    // Fetch drama details
    const { drama, chapters, isLoading: dramaLoading } = useDramaDetail(bookId);

    // Calculate batch episode (fetch 5 episodes at a time)
    const batchEpisode = Math.floor(currentIndex / 5) * 5 + 1;

    // Fetch video URLs for current batch
    const { videos, isLoading: videosLoading, isError: videosError } = useChapters(bookId, 720, batchEpisode);
    
    // Debug logs removed for production

    useEffect(() => {
        if (chapters && chapters.length > 0) {
            setEpisodes(chapters);

            // Find initial index if startChapter provided
            if (startChapter) {
                const foundIndex = chapters.findIndex(ch => String(ch.id) === String(startChapter));
                if (foundIndex >= 0) {
                    setCurrentIndex(foundIndex);
                }
            }
        }
    }, [chapters, startChapter]);

    useEffect(() => {
        // Update page title and meta
        if (title) {
            updatePageTitle(`${title} — Nonton Drama China Sub Indonesia | DinoShort`);
            updateMetaDescription(`Tonton ${title} gratis sub Indonesia di DinoShort. Episode lengkap kualitas HD.`);
        }
    }, [title]);

    useEffect(() => {
        // Apply mobile fullscreen mode
        if (isMobileMode && isMobile()) {
            document.body.classList.add('mobile-fullscreen-player', 'player-open');
            return () => {
                document.body.classList.remove('mobile-fullscreen-player', 'player-open');
            };
        }
    }, [isMobileMode]);

    // Watch History - Save watch position (same approach as old project)
    const saveWatchPosition = useCallback(async (videoEl) => {
        try {
            const token = getUserToken();
            
            if (!token || !bookId || !episodes[currentIndex] || isPlayingAd) {
                return;
            }

            const currentEpisode = episodes[currentIndex];
            const chapterId = currentEpisode?.id || currentIndex + 1;
            const durationSeconds = Math.floor(videoEl.duration || 0);
            const positionSeconds = Math.floor(videoEl.currentTime || 0);
            
            if (!durationSeconds || positionSeconds <= 0) {
                return;
            }

            const payload = {
                bookId: String(bookId),
                chapterId: String(chapterId),
                positionSeconds,
                durationSeconds,
                bookName: title || drama?.bookName || `Drama ${bookId}`,
                coverUrl: getCoverImageUrl(drama)
            };

            const response = await fetch(api('/api/user/watch-history'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            
            if (!response.ok || !result.success) {
                console.error('[Watch History] Save failed:', result);
            }
        } catch (error) {
            console.error('[Watch History] Save error:', error);
        }
    }, [bookId, currentIndex, episodes, isPlayingAd, title, drama]);

    // Watch History - Setup event listeners on video element
    useEffect(() => {
        const videoEl = videoRef.current;
        
        if (!videoEl || !bookId || !episodes[currentIndex]) {
            return;
        }

        // Save on timeupdate (every 5 seconds like old project)
        const handleTimeUpdate = () => {
            if (isPlayingAd) return;
            
            const currentTime = Math.floor(videoEl.currentTime);
            const lastSaveTime = lastSaveTimeRef.current;
            
            // Save every 5 seconds
            if (currentTime % 5 === 0 && currentTime !== lastSaveTime) {
                lastSaveTimeRef.current = currentTime;
                saveWatchPosition(videoEl);
            }
        };

        // Save when video ends
        const handleEnded = () => {
            saveWatchPosition(videoEl);
        };

        // Save when pausing (defensive)
        const handlePause = () => {
            if (!isPlayingAd && videoEl.currentTime > 0) {
                saveWatchPosition(videoEl);
            }
        };

        videoEl.addEventListener('timeupdate', handleTimeUpdate);
        videoEl.addEventListener('ended', handleEnded);
        videoEl.addEventListener('pause', handlePause);

        // Cleanup
        return () => {
            videoEl.removeEventListener('timeupdate', handleTimeUpdate);
            videoEl.removeEventListener('ended', handleEnded);
            videoEl.removeEventListener('pause', handlePause);
            // Final save on unmount
            if (videoEl.currentTime > 0 && !isPlayingAd) {
                saveWatchPosition(videoEl);
            }
        };
    }, [bookId, currentIndex, episodes, isPlayingAd, saveWatchPosition]);

    // Reset watch tracking when episode changes
    useEffect(() => {
        watchTimeRef.current = 0;
        lastLoggedTime.current = -1;
        setCurrentEpisodeWatched(false);
        
        // Cleanup old handler if exists
        return () => {
            const videoEl = videoRef.current;
            if (videoEl && watchTimeHandlerRef.current) {
                videoEl.removeEventListener('timeupdate', watchTimeHandlerRef.current);
                watchTimeHandlerRef.current = null;
            }
        };
    }, [currentIndex, isPlayingAd]);

    // Watch time handler - called when video is actually loaded
    const setupWatchTimeTracking = useCallback((videoElement) => {
        if (!videoElement || isPlayingAd) return;

        // Remove old handler if exists
        if (watchTimeHandlerRef.current) {
            videoElement.removeEventListener('timeupdate', watchTimeHandlerRef.current);
        }
        
        // Use ref to track if we've marked as watched to avoid checking state
        const markedWatched = { current: false };
        
        const handleWatchTimeUpdate = function() {
            const videoEl = this;
            if (!videoEl) return;
            
            const currentTime = Math.floor(videoEl.currentTime);
            watchTimeRef.current = currentTime;
            lastLoggedTime.current = currentTime;
            
            // Mark as "watched" after 5 seconds
            if (currentTime >= 5 && !markedWatched.current) {
                markedWatched.current = true;
                setCurrentEpisodeWatched(true);
            }
        };
        
        videoElement.addEventListener('timeupdate', handleWatchTimeUpdate);
        watchTimeHandlerRef.current = handleWatchTimeUpdate;
    }, [currentIndex, isPlayingAd]);

    // Helper function to increment episode count and check for ads
    const incrementEpisodeCountAndCheckAd = useCallback(() => {
        if (isPlayingAd) return;
        
        const newCount = episodeWatchCount + 1;
        setEpisodeWatchCount(newCount);
        
        // Check if should show ad (every 3 episodes for non-VIP)
        const shouldShowAd = !isVIP && newCount % 3 === 0 && ads && ads.length > 0;
        
        return { newCount, shouldShowAd };
    }, [episodeWatchCount, isVIP, ads, isPlayingAd]);

    const handlePrevious = () => {
        if (currentIndex > 0) {
            if (currentEpisodeWatched && !isPlayingAd) {
                const result = incrementEpisodeCountAndCheckAd();
                if (result?.shouldShowAd && ads && ads.length > 0) {
                    const adIndex = Math.floor((result.newCount / 3 - 1) % ads.length);
                    setCurrentAd(ads[adIndex]);
                    setIsPlayingAd(true);
                    setPendingEpisodeIndex(currentIndex - 1);
                    return;
                }
            }
            setCurrentIndex(currentIndex - 1);
            setCurrentEpisodeWatched(false);
            watchTimeRef.current = 0;
        }
    };

    const handleNext = () => {
        if (currentIndex < episodes.length - 1) {
            if (currentEpisodeWatched && !isPlayingAd) {
                const result = incrementEpisodeCountAndCheckAd();
                if (result?.shouldShowAd && ads && ads.length > 0) {
                    const adIndex = Math.floor((result.newCount / 3 - 1) % ads.length);
                    setCurrentAd(ads[adIndex]);
                    setIsPlayingAd(true);
                    setPendingEpisodeIndex(currentIndex + 1);
                    return;
                }
            }
            setCurrentIndex(currentIndex + 1);
            setCurrentEpisodeWatched(false);
            watchTimeRef.current = 0;
        }
    };

    const handleEpisodeClick = (index) => {
        if (currentEpisodeWatched && !isPlayingAd) {
            const result = incrementEpisodeCountAndCheckAd();
            if (result?.shouldShowAd && ads && ads.length > 0) {
                const adIndex = Math.floor((result.newCount / 3 - 1) % ads.length);
                setCurrentAd(ads[adIndex]);
                setIsPlayingAd(true);
                setPendingEpisodeIndex(index);
                setShowEpisodeList(false);
                return;
            }
        }
        setCurrentIndex(index);
        setCurrentEpisodeWatched(false);
        watchTimeRef.current = 0;
        setShowEpisodeList(false);
    };

    const toggleEpisodeList = () => {
        setShowEpisodeList(!showEpisodeList);
    };

    const handleVideoEnded = () => {
        if (isPlayingAd) {
            setIsPlayingAd(false);
            setCurrentAd(null);
            if (pendingEpisodeIndex !== null) {
                setCurrentIndex(pendingEpisodeIndex);
                setCurrentEpisodeWatched(false);
                watchTimeRef.current = 0;
                setPendingEpisodeIndex(null);
            }
        } else {
            const newCount = episodeWatchCount + 1;
            setEpisodeWatchCount(newCount);
            setCurrentEpisodeWatched(true);
            
            const shouldShowAd = !isVIP && newCount % 3 === 0 && ads && ads.length > 0;
            
            if (shouldShowAd && autoPlay && currentIndex < episodes.length - 1) {
                const adIndex = Math.floor((newCount / 3 - 1) % ads.length);
                setCurrentAd(ads[adIndex]);
                setIsPlayingAd(true);
                setPendingEpisodeIndex(currentIndex + 1);
            } else if (autoPlay && currentIndex < episodes.length - 1) {
                setTimeout(() => {
                    setCurrentIndex(currentIndex + 1);
                    setCurrentEpisodeWatched(false);
                    watchTimeRef.current = 0;
                }, 500);
            }
        }
    };

    const handleVideoError = (e, errorContext = {}) => {
        const newCount = videoErrorCount + 1;
        setVideoErrorCount(newCount);
        
        const errorMsg = e?.target?.error?.message || e?.message || 'Video playback failed';
        setLastErrorMessage(errorMsg);
        
        // Show reset suggestion after 3 consecutive errors
        if (newCount >= 3) {
            setShowResetSuggestion(true);
        }
    };

    const handleVideoSuccess = () => {
        if (videoErrorCount > 0) {
            setVideoErrorCount(0);
            setShowResetSuggestion(false);
        }
    };

    const handleResetPlayer = () => {
        if (!canReset) {
            alert(`Reset sedang dalam cooldown. Tunggu ${resetCooldownTime} menit lagi untuk mencegah spam dan ban.`);
            return;
        }
        
        // Clearing localStorage and reloading
        
        // Save reset time before clearing
        const resetTime = Date.now();
        localStorage.setItem('lastResetTime', resetTime.toString());
        
        // Clear all except lastResetTime
        const lastReset = localStorage.getItem('lastResetTime');
        localStorage.clear();
        localStorage.setItem('lastResetTime', lastReset);
        
        window.location.reload();
    };

    // Check reset cooldown on mount
    useEffect(() => {
        const lastResetTime = localStorage.getItem('lastResetTime');
        if (lastResetTime) {
            const timeSinceReset = Date.now() - parseInt(lastResetTime);
            const cooldownMs = 10 * 60 * 1000; // 10 minutes
            
            if (timeSinceReset < cooldownMs) {
                setCanReset(false);
                const remainingTime = Math.ceil((cooldownMs - timeSinceReset) / 1000 / 60);
                setResetCooldownTime(remainingTime);
                
                // Set timeout to enable reset after cooldown
                const timeoutId = setTimeout(() => {
                    setCanReset(true);
                    setResetCooldownTime(0);
                }, cooldownMs - timeSinceReset);
                
                return () => clearTimeout(timeoutId);
            }
        }
    }, []);

    if (!bookId) {
        return (
            <div className="player-page">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#999' }}>
                    <div style={{ textAlign: 'center' }}>
                        <h3>Drama tidak ditemukan</h3>
                        <p>Kembali ke homepage untuk memilih drama</p>
                    </div>
                </div>
            </div>
        );
    }

    if (dramaLoading) {
        return (
            <div className="player-page">
                <div className="loading-spinner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                    <div>
                        <div className="spinner"></div>
                        <p>Memuat video...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="player-page">
            {/* Banner Ads for non-mobile */}
            {!isMobileMode && <BannerAds />}

            {/* Back Button (Desktop) */}
            {!isMobileMode && (
                <div className="back-button-container">
                    <button
                        className="back-btn"
                        onClick={() => window.history.back()}
                    >
                        <i className="fas fa-arrow-left"></i>
                        <span>Kembali</span>
                    </button>
                </div>
            )}

            {/* Smart Error Detection - Reset Suggestion (Mobile-Friendly) */}
            {showResetSuggestion && (
                <div className="error-suggestion-overlay" onClick={() => setShowResetSuggestion(false)}>
                    <div className="error-suggestion-box" onClick={(e) => e.stopPropagation()}>
                        <div className="error-icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h3>Video Tidak Dapat Diputar</h3>
                        <p>Terdeteksi {videoErrorCount} error berturut-turut.</p>
                        <p className="error-hint">
                            {canReset 
                                ? "Coba reset player untuk refresh token dan cache." 
                                : `Reset dalam cooldown (${resetCooldownTime} menit). Ini mencegah spam dan ban.`
                            }
                        </p>
                        <div className="error-actions">
                            <button 
                                className="error-reset-btn"
                                onClick={handleResetPlayer}
                                disabled={!canReset}
                                style={{ opacity: canReset ? 1 : 0.5 }}
                            >
                                <i className="fas fa-sync-alt"></i> {canReset ? 'Reset Player' : `Cooldown ${resetCooldownTime}m`}
                            </button>
                            <button 
                                className="error-dismiss-btn"
                                onClick={() => setShowResetSuggestion(false)}
                            >
                                Tutup
                            </button>
                        </div>
                        {lastErrorMessage && (
                            <p className="error-detail">Error: {lastErrorMessage}</p>
                        )}
                        {!canReset && (
                            <p className="error-cooldown-info">
                                <i className="fas fa-clock"></i> Cooldown mencegah terlalu banyak request ke server dan menghindari ban IP.
                            </p>
                        )}
                    </div>
                </div>
            )}

            <main className="player-container" style={{ height: isMobileMode ? '100vh' : 'auto' }}>
                {/* Video Container */}
                <div className={`video-container ${isPlayingAd ? 'playing-ad' : ''}`} id="videoContainer">
                    <div className="player-watermark">DinoShort</div>
                    <button
                        className="player-back-btn"
                        onClick={() => window.history.back()}
                        aria-label="Kembali"
                    >
                        <i className="fas fa-times"></i>
                    </button>

                    {isPlayingAd && currentAd ? (
                        // Playing video ad - clickable to open URL
                        <div 
                            className="video-item active ad-playing"
                            onClick={() => {
                                const clickUrl = currentAd.click_url || currentAd.target_url;
                                if (clickUrl) {
                                    window.open(clickUrl, '_blank', 'noopener,noreferrer');
                                }
                            }}
                            style={{ cursor: (currentAd.click_url || currentAd.target_url) ? 'pointer' : 'default' }}
                        >
                            <div className="ad-watermark">
                                <i className="fas fa-bullhorn"></i> IKLAN
                            </div>
                            {(currentAd.click_url || currentAd.target_url) && (
                                <div className="ad-click-hint">
                                    <i className="fas fa-external-link-alt"></i> Klik untuk info lebih lanjut
                                </div>
                            )}
                            <video
                                ref={videoRef}
                                key={`ad-${currentAd.id}`}
                                preload="auto"
                                playsInline
                                webkit-playsinline="true"
                                controls={false}
                                autoPlay
                                onLoadedData={() => {}}
                                onEnded={handleVideoEnded}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                            >
                                <source src={currentAd.file_url || currentAd.video_url} type="video/mp4" />
                            </video>
                        </div>
                    ) : videosLoading ? (
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>Memuat video...</p>
                        </div>
                    ) : (() => {
                        const videoIndex = currentIndex % 5;
                        const currentVideo = videos?.[videoIndex];
                        const videoPath = currentVideo?.videoPath;
                        
                        // Check if video path is valid (not "N/A" and exists)
                        const isValidVideo = videoPath && videoPath !== 'N/A' && videoPath.startsWith('http');
                        
                        if (isValidVideo) {
                            return (
                        <div className="video-item active">
                            <video
                                        ref={videoRef}
                                        key={`${currentIndex}-${videoPath}`}
                                preload="metadata"
                                playsInline
                                webkit-playsinline="true"
                                controls
                                autoPlay
                                        onLoadedData={(e) => {
                                            handleVideoSuccess();
                                            setupWatchTimeTracking(e.target);
                                        }}
                                        onEnded={handleVideoEnded}
                                        onError={(e) => {
                                            handleVideoError(e, {
                                                source: videoPath,
                                                currentIndex,
                                                videoIndex,
                                                batchEpisode,
                                                timestamp: new Date().toISOString()
                                            });
                                        }}
                                        onStalled={(e) => {
                                            handleVideoError(e, {
                                                type: 'stalled',
                                                source: videoPath,
                                                currentIndex
                                            });
                                        }}
                                        onSuspend={() => {}}
                                        onAbort={(e) => {
                                            handleVideoError(e, {
                                                type: 'aborted',
                                                source: videoPath,
                                                currentIndex
                                            });
                                        }}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            >
                                        <source src={videoPath} type="video/mp4" />
                                        Browser tidak mendukung video ini
                            </video>
                        </div>
                            );
                        } else {
                            // Video tidak valid - trigger error
                            if (videoErrorCount < 3) {
                                setTimeout(() => {
                                    handleVideoError(
                                        { message: 'Invalid video path or video not available' },
                                        {
                                            type: 'invalid_video',
                                            videoPath,
                                            currentIndex,
                                            videoIndex
                                        }
                                    );
                                }, 100);
                            }
                            
                            return (
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                    <p style={{ color: '#ff6b6b', marginTop: '1rem' }}>
                                        {videosError 
                                            ? 'Gagal memuat video. Coba refresh halaman.' 
                                            : videos?.length === 0
                                            ? 'Video tidak ditemukan untuk episode ini.'
                                            : !currentVideo
                                            ? `Video index ${videoIndex} tidak ada dalam batch.`
                                            : videoPath === 'N/A'
                                            ? 'Video tidak tersedia untuk kualitas ini.'
                                            : 'Video tidak dapat diputar. Silakan coba episode lain.'
                                        }
                                    </p>
                                    <p style={{ color: '#999', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                        Episode: {currentIndex + 1} | Batch: {batchEpisode} | Index: {videoIndex}
                                    </p>
                                    <button 
                                        onClick={() => {
                                            setShowResetSuggestion(true);
                                        }}
                                        style={{
                                            marginTop: '1rem',
                                            padding: '0.75rem 1.5rem',
                                            background: 'rgba(255, 107, 107, 0.2)',
                                            border: '1px solid #ff6b6b',
                                            borderRadius: '8px',
                                            color: '#ff6b6b',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        🔄 Tampilkan Reset
                                    </button>
                                </div>
                            );
                        }
                    })()}
                </div>

                {/* Side Panel (Desktop) */}
                {!isMobileMode && (
                    <div className="side-panel">
                        <div className="video-info">
                            <h3>{title || 'Loading...'}</h3>
                            <p>{drama?.introduction || 'Episode drama'}</p>
                            <div className="video-stats">
                                <span>Episode {currentIndex + 1} / {episodes.length}</span>
                            </div>
                            
                            {/* Auto Play Toggle */}
                            <div className="autoplay-toggle" style={{ marginTop: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={autoPlay}
                                        onChange={(e) => setAutoPlay(e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <span style={{ color: '#999', fontSize: '0.9rem' }}>
                                        <i className="fas fa-forward"></i> Auto Play Episode Berikutnya
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Navigation Controls */}
                        <div className="controls">
                            <button
                                className="control-btn"
                                onClick={handlePrevious}
                                disabled={currentIndex === 0}
                            >
                                <i className="fas fa-chevron-up"></i>
                                <span>Sebelumnya</span>
                            </button>
                            <button
                                className="control-btn"
                                onClick={handleNext}
                                disabled={currentIndex >= episodes.length - 1}
                            >
                                <i className="fas fa-chevron-down"></i>
                                <span>Selanjutnya</span>
                            </button>
                        </div>

                        {/* Episode List */}
                        <div className="episode-section">
                            <h4>Episode Lainnya</h4>
                            <div className="episode-list">
                                {episodes.map((episode, index) => (
                                    <div
                                        key={episode.id}
                                        className={`episode-item ${index === currentIndex ? 'active' : ''}`}
                                        onClick={() => handleEpisodeClick(index)}
                                    >
                                        <span>Episode {index + 1}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile Bottom Controls (NO RESET BUTTON - only in error overlay) */}
                {isMobileMode && (
                    <>
                    <div className="mobile-controls">
                        <button
                            className="mobile-control-btn"
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                        >
                            <i className="fas fa-step-backward"></i>
                        </button>
                        <button
                            className="mobile-control-btn"
                            onClick={handleNext}
                            disabled={currentIndex >= episodes.length - 1}
                        >
                            <i className="fas fa-step-forward"></i>
                        </button>
                            <button className="mobile-control-btn" onClick={toggleEpisodeList}>
                            <i className="fas fa-list"></i>
                        </button>
                    </div>

                        {/* Mobile Episode List Overlay */}
                        {showEpisodeList && (
                            <div className="mobile-episode-overlay" onClick={() => setShowEpisodeList(false)}>
                                <div className="mobile-episode-panel" onClick={(e) => e.stopPropagation()}>
                                    <div className="mobile-episode-header">
                                        <h4>Pilih Episode</h4>
                                        <button className="close-episode-btn" onClick={() => setShowEpisodeList(false)}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>

                                    {/* Mobile Auto Play Toggle */}
                                    <div className="mobile-autoplay-toggle">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={autoPlay}
                                                onChange={(e) => setAutoPlay(e.target.checked)}
                                            />
                                            <span>
                                                <i className="fas fa-forward"></i> Auto Play Episode Berikutnya
                                            </span>
                                        </label>
                                    </div>

                                    <div className="mobile-episode-list">
                                        {episodes.map((episode, index) => (
                                            <div
                                                key={episode.id}
                                                className={`mobile-episode-item ${index === currentIndex ? 'active' : ''}`}
                                                onClick={() => handleEpisodeClick(index)}
                                            >
                                                <i className="fas fa-play-circle"></i>
                                                <span>Episode {index + 1}</span>
                                                {index === currentIndex && <i className="fas fa-check"></i>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

export default Player;
